import 'react-big-calendar/lib/css/react-big-calendar.css'

/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { ExternalStyles, Theme, useTheme } from 'bold-ui'
import { pink } from 'bold-ui/lib/styles/colors'
import { add, max, startOfDay, sub } from 'date-fns'
import { parseISO } from 'date-fns/esm'
import React, { useMemo } from 'react'
import { Calendar as BigCalendar, CalendarProps, Components, View } from 'react-big-calendar'

import { AgendaEvent } from './AgendaEvent'
import { AgendaEventProps, AgendaEventWrapper } from './AgendaEventWrapper'
import agendaLocalizer, { defaultMessages } from './agendaLocalizer'
import { AgendaTimeSlotWrapper } from './AgendaTimeSlotWrapper'
import { AgendaToolbar } from './AgendaToolbar'
import computeAvailableSlots from './computeAvailableSlots'
import { AgendaEventModel, AgendaMessages, WeekTimeIntervals } from './model'

export interface AgendaProps<TEvent extends AgendaEventModel> {
  date?: Date
  events?: AgendaEventModel[]
  view?: View
  availableTimes?: WeekTimeIntervals
  minutesStep?: number
  disablePastDates?: boolean
  style?: ExternalStyles

  messages?: AgendaMessages
  eventComponent?: React.ComponentType<AgendaEventProps<TEvent>>

  onSelectSlot?(start: Date, end: Date): void
  onNavigate?(newStart: Date, newEnd: Date): void
}

export function Agenda<TEvent extends AgendaEventModel = AgendaEventModel>(props: AgendaProps<TEvent>) {
  const {
    date = new Date(),
    events = [],
    minutesStep = 30,
    disablePastDates = true,
    eventComponent = AgendaEvent,
    messages = defaultMessages,
    availableTimes = {},
    view,
    style: externalStyles,
    onSelectSlot,
    onNavigate,
  } = props

  const theme = useTheme()
  const styles = useMemo(() => createStyles(theme), [theme])
  const { isAvailable, minTime, maxTime } = useMemo(() => computeAvailableSlots(availableTimes, disablePastDates), [
    availableTimes,
    disablePastDates,
  ])

  const handleSelectSlot =
    onSelectSlot &&
    (({ start, end }) =>
      !isAvailable({ start, end }) ? false : onSelectSlot(parseDateOrString(start), parseDateOrString(end)))

  const handleNavigate = onNavigate && ((newDate: Date) => onNavigate(minTime(newDate), maxTime(newDate)))

  return (
    <BigCalendar
      defaultDate={!onNavigate && date}
      date={onNavigate && date}
      events={events}
      view={view}
      min={max([sub(minTime(date), { minutes: minutesStep }), startOfDay(date)])}
      max={maxTime(date)}
      step={minutesStep}
      timeslots={1}
      css={css(styles, externalStyles)}
      localizer={agendaLocalizer}
      defaultView='day'
      culture='pt-BR'
      {...accessors}
      components={{
        ...calendarComponents,
        eventWrapper: (props) => React.createElement(AgendaEventWrapper, { ...props, children: eventComponent }),
        timeSlotWrapper: (props) =>
          React.createElement(AgendaTimeSlotWrapper as any, {
            ...props,
            isAvailable: isAvailable({
              start: (props as any).value,
              end: add((props as any).value as Date, { minutes: minutesStep }),
            }),
            disablePastDates: disablePastDates,
            messages: messages,
          }),
      }}
      selectable={!!onSelectSlot}
      onSelecting={
        onSelectSlot &&
        (({ start, end }) => isAvailable({ start: parseDateOrString(start), end: parseDateOrString(end) }))
      }
      onSelectSlot={handleSelectSlot}
      onNavigate={handleNavigate}
    />
  )
}

const calendarComponents: Components<AgendaEventModel> = {
  toolbar: AgendaToolbar,
}

const accessors: Partial<CalendarProps<AgendaEventModel>> = {
  startAccessor: 'start',
  endAccessor: 'end',
  titleAccessor: 'title',
  allDayAccessor: () => false,
}

const parseDateOrString = (date: Date | string) => (typeof date === 'string' ? parseISO(date) : date)

const createStyles = (theme: Theme) => css`
  .rbc-time-content {
    padding: 2.5rem 0;
  }

  .rbc-events-container {
    margin-right: 0;
  }

  .rbc-timeslot-group,
  .rbc-events-container,
  .rbc-time-view {
    border-width: 0;
  }

  .rbc-time-slot {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    min-height: 3.5rem;

    .rbc-label {
      position: relative;
      top: -0.55rem;
    }
  }

  .rbc-time-content {
    border-top: 1px solid ${theme.pallete.divider};
  }

  .rbc-day-slot .rbc-time-slot {
    border-top-color: ${theme.pallete.divider};
  }

  .rbc-allday-cell {
    display: none;
  }

  .rbc-time-view .rbc-header {
    border-bottom: none;
  }

  .rbc-current-time-indicator {
    height: 2px;
    background-color: ${pink.c40};

    &::before {
      content: '';
      display: inline-block;
      width: 0.5rem;
      height: 0.5rem;
      border-radius: 0.25rem;
      background-color: ${pink.c40};
      position: relative;
      top: -0.55rem;
      left: -0.5rem;
    }
  }

  .rbc-today {
    background-color: transparent;
  }

  .rbc-time-slot-disabled {
    cursor: not-allowed;
    z-index: 1;
  }

  .rbc-time-slot-available {
    cursor: pointer;
    position: relative;
    z-index: 1;
  }
`
